import React, { useEffect, useMemo, useState } from 'react';
import { filter, forEach, isEmpty, isSearchedByKeyword, map, trim } from '@libs/shared';
import classNames from 'classnames';
import { FIELD_TYPE_MAP, Intl } from '../../constants';
import { SearchInput } from '@fx-ui/jdy-design-biz';
import FieldTypeItem from '../field-type-item';
import HighLightText from '../highlight-text';

interface CustomField {
    className?: string;
    text: string;
    value: string;
    fieldType: string;
    id: string;
    hasAffix?: boolean;
}

interface IFieldSelectList {
    fieldItems: CustomField[];
    autoSelect?: boolean;
    hasSubForm?: boolean;
    onFieldSelect: (item: any) => void;
    supportFieldType?: boolean;
    hasIcon?: boolean;
    placeHolder?: string;
    className?: string;
    passTypeCheck?: boolean;
    styleMap?: Record<string, any>;
    fieldTypeMap?: Record<string, any>;
}

/**
 * 字段选择列表组件
 * 带搜索
 *
 * @class
 * @param props IFieldSelectList
 */
const FieldSelectList: React.FC<IFieldSelectList> = (props) => {
    const {
        fieldItems,
        onFieldSelect,
        className = '',
        placeHolder = Intl.get('search'),
        supportFieldType = true,
        autoSelect = true,
        hasSubForm = false,
        passTypeCheck = false,
        styleMap,
        fieldTypeMap = FIELD_TYPE_MAP
    } = props;
    const [searching, setSearching] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [items, setItems] = useState<(CustomField & { valType: string })[]>([]);

    useEffect(() => {
        setItems(initFieldItems(fieldItems));
    }, [fieldItems]);

    // 根据搜索关键字过滤字段
    const showItems = useMemo(() => {
        return isEmpty(keyword)
            ? items
            : filter(items, (item) => isSearchedByKeyword(item.text, keyword));
    }, [items, keyword]);

    const initFieldItems = (fieldItems: any) => {
        const result: any[] = [];
        forEach(fieldItems, function (item) {
            if (hasSubForm && item.subform) {
                result.push({
                    name: item.name,
                    text: item.text,
                    type: 'array',
                    icon: item.icon,
                    id: item.id,
                    valType: 'array'
                });
            } else {
                const valType = fieldTypeMap[item.type];
                if (valType || passTypeCheck) {
                    result.push({
                        name: item.name,
                        text: item.text,
                        type: item.type,
                        icon: item.icon,
                        id: item.id,
                        valType
                    });
                }
            }
        });
        return result;
    };

    const renderList = () => {
        if (searching) {
            return (
                <li className="field-search-empty">
                    <span className="searching">{Intl.get('searching')}</span>
                </li>
            );
        }
        if (isEmpty(showItems)) {
            if (!isEmpty(keyword)) {
                return (
                    <li className="field-search-empty">
                        <span>{Intl.get('formula.field.search.empty')}</span>
                    </li>
                );
            } else {
                return (
                    <li className="field-search-empty">
                        <span>{Intl.get('data.select.empty')}</span>
                    </li>
                );
            }
        } else {
            return (
                <ul className="field-list">
                    {map(showItems, (comboItem, i) => {
                        return (
                            <div
                                key={i}
                                className="colorful-field-item"
                                onClick={() => {
                                    onFieldSelect && onFieldSelect(comboItem);
                                }}
                            >
                                <div className="field-name">
                                    <HighLightText text={comboItem.text} keyword={keyword.trim()} />
                                </div>
                                {supportFieldType && (
                                    <FieldTypeItem styleMap={styleMap} type={comboItem.valType} />
                                )}
                            </div>
                        );
                    })}
                </ul>
            );
        }
    };

    return (
        <div className={classNames(className, 'fx-field-select-list')}>
            <SearchInput
                autoFocus={autoSelect}
                className="field-search"
                inputClassName="inner-input"
                onChange={() => setSearching(true)}
                placeholder={placeHolder}
                onSearchStart={(value: string) => {
                    setKeyword(trim(value));
                    setSearching(false);
                }}
            />
            {renderList()}
        </div>
    );
};

export default FieldSelectList;
